import './Penalties.scss';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useProtectedInterval } from '../../hooks/useProtectedInterval';
import Toast from '../../components/Toast/Toast';
import Pagination from '../../components/Layout/Pagination';
import EmptyState from '../../components/Layout/EmptyState';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

const PENALTY_STATUS = {
    '1': { label: 'Создан', bg: 'rgba(245,158,11,0.12)', color: '#f59e0b', rowBg: 'rgba(245,158,11,0.04)' },
    '2': { label: 'Списан', bg: 'rgba(239,68,68,0.12)', color: '#ef4444', rowBg: 'rgba(239,68,68,0.04)' },
    '3': { label: 'Отменён', bg: 'rgba(34,197,94,0.12)', color: '#22c55e', rowBg: 'rgba(34,197,94,0.04)' },
};

const formatPenaltyDate = (isoStr) => {
    if (!isoStr) return '--';
    try {
        const d = new Date(isoStr);
        const day = String(d.getDate()).padStart(2, '0');
        const mon = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        const h = String(d.getHours()).padStart(2, '0');
        const m = String(d.getMinutes()).padStart(2, '0');
        return { date: `${day}.${mon}.${year}`, time: `${h}:${m}` };
    } catch { return { date: isoStr, time: '' }; }
};

export default function Penalties() {
    const { t } = useTranslation('penalties');

    const theme = useSelector((state) => state.APP_THEME);
    const api_url = useSelector((state) => state.API_URL);

    const jwtToken = localStorage.getItem('accessToken');
    const navigate = useNavigate();
    const [penaltiesData, setPenaltiesData] = useState([]);
    const [errorFetchingPenalties, setErrorFetchingPenalties] = useState(false);

    const [pageSize, setPageSize] = useState(10);
    const [pageIndex, setPageIndex] = useState(1);
    const [reqRowsCount, setReqRowsCount] = useState(0);

    useEffect(() => {
        if (!jwtToken) {
            navigate('/auth');
            localStorage.clear();
        }
    }, [jwtToken, navigate]);

    const getPenaltiesData = useCallback(async () => {
        const jwtToken = localStorage.getItem('accessToken');
        try {
            const response = await fetch(api_url + `exchange/penaltyuser/info?page=${pageIndex}&page_size=${pageSize}`, {
                headers: {
                    'Authorization': `JWT ${jwtToken}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.clear();
                    navigate('/auth');
                }
            }
            const data = await response.json();
            setPenaltiesData(data);
            setReqRowsCount(data.count);
        } catch (error) {
            setErrorFetchingPenalties(true);
        }
    }, [api_url, pageIndex, pageSize, navigate]);

    useProtectedInterval(getPenaltiesData, [pageSize, pageIndex]);

    const results = penaltiesData.results || [];

    return (
        <div className={theme === 'light' ? "Penalties" : "Penalties dark"}>
            <Toast show={errorFetchingPenalties} type="error" message={t('penalties:errors.fetchError')} onClose={() => setErrorFetchingPenalties(false)} />
            <div className="content">
                <Pagination
                    pageIndex={pageIndex}
                    totalCount={reqRowsCount}
                    pageSize={pageSize}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    pageSizes={[5, 10, 15]}
                />
                {results.length === 0 ? (
                    <EmptyState title={t('penalties:empty.title')} description={t('penalties:empty.description')} />
                ) : (
                    <Paper className="penalty-table-paper" sx={{ width: '100%', overflow: 'hidden', background: theme === 'light' ? 'white' : 'rgba(31, 38, 48, 1)', borderRadius: '12px' }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="penalty-th">{t('penalties:headers.status')}</TableCell>
                                        <TableCell className="penalty-th">{t('penalties:headers.amountRub')}</TableCell>
                                        <TableCell className="penalty-th">{t('penalties:headers.amountUsdt')}</TableCell>
                                        <TableCell className="penalty-th">{t('penalties:headers.comment')}</TableCell>
                                        <TableCell className="penalty-th">{t('penalties:headers.date')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {results.map((item, index) => {
                                        const st = PENALTY_STATUS[item.status?.value] || {};
                                        const dt = formatPenaltyDate(item.created?.value);
                                        return (
                                            <TableRow key={item.pk || index} style={{ background: st.rowBg }} className="penalty-row">
                                                <TableCell>
                                                    <span className="penalty-badge" style={{ background: st.bg, color: st.color }}>
                                                        {t('penalties:statuses.' + item.status?.value) || item.status?.value}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="penalty-amount">{Number(item.rur?.value).toLocaleString('ru-RU')}</span>
                                                    <span className="penalty-currency"> RUB</span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="penalty-usdt">{item.amount?.value}</span>
                                                    <span className="penalty-currency"> USDT</span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="penalty-comment">{item.comment?.value}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="penalty-date">
                                                        <span className="penalty-date-value">{dt.date}</span>
                                                        <span className="penalty-date-time">{dt.time}</span>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}
                <Pagination
                    pageIndex={pageIndex}
                    totalCount={reqRowsCount}
                    pageSize={pageSize}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    pageSizes={[5, 10, 15]}
                />
            </div>
        </div>
    )
}
